import apiClient from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import type {
    GetStoresByBrandResponse,
    UpdateStoreLiveStatusRequest,
    UpdateStoreLiveStatusResponse,
    StoreLiveStatus,
    StoreSearchResult,
    GetBrandAgentConfigResponse,
    SaveBrandAgentConfigRequest,
    SaveBrandAgentConfigResponse,
    GetSaleCompletionWhatsAppConfigResponse,
    UpdateSaleCompletionWhatsAppConfigRequest,
    UpdateSaleCompletionWhatsAppConfigResponse,
    GetMultiStoreAgentConfigResponse,
    UpdateMultiStoreAgentConfigRequest,
    UpdateMultiStoreAgentConfigResponse,
    SaleCompletionWhatsAppConfig,
    MultiStoreAgentConfigPayload,
    GetLoginPolicyResponse,
    SaveLoginPolicyRequest,
    GetMovementCountConfigResponse,
    SaveMovementCountConfigRequest,
    SaveMovementCountConfigResponse,
    GetStoreMembersRequest,
    GetStoreMembersResponse,
    StoreMember,
    DailyNotificationSettings,
    GetDailyNotificationSettingsResponse,
    UpdateDailyNotificationSettingsResponse,
    StoreOperationsConfig,
    StoreBrandOperationsConfig,
    GetStoreOperationsConfigResponse,
    GetStoreBrandOperationsConfigResponse,
    GetDashboardExpiryResponse,
    UpdateDashboardExpiryRequest,
    UpdateDashboardExpiryResponse,
} from '../types/api.types';

const brandHeaders = (storeBrandId: string) => ({
    headers: { 'X-Store-Brand-Id': storeBrandId },
});

/**
 * Get all stores for a store brand
 * @param storeBrandId - MongoDB ObjectId of the store brand
 */
export const getStoresByBrand = async (storeBrandId: string): Promise<StoreSearchResult[]> => {
    const response = await apiClient.get<GetStoresByBrandResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_STORES_BY_BRAND(storeBrandId),
        {
            headers: {
                'X-Store-Brand-Id': storeBrandId,
            },
        }
    );

    return response.data || [];
};

/**
 * Update a store's live status (Live or Trial)
 * @param storeId - MongoDB ObjectId of the store
 * @param storeStatus - New status: 'Live' or 'Trial'
 * @param storeBrandId - MongoDB ObjectId of the store brand (needed for admin context routing)
 */
export const updateStoreLiveStatus = async (
    storeId: string,
    storeStatus: StoreLiveStatus,
    storeBrandId: string
): Promise<UpdateStoreLiveStatusResponse> => {
    const requestBody: UpdateStoreLiveStatusRequest = {
        storeStatus,
        storeBrandId,
    };

    const response = await apiClient.patch<UpdateStoreLiveStatusResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.UPDATE_LIVE_STATUS(storeId),
        requestBody,
        {
            headers: {
                'X-Store-Brand-Id': storeBrandId,
            },
        }
    );

    return response;
};

export const getBrandAgentConfig = async (
    storeBrandId: string
): Promise<GetBrandAgentConfigResponse> => {
    return apiClient.post<GetBrandAgentConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_BRAND_AGENT_CONFIG(),
        { storeBrandId },
        { headers: { 'X-Store-Brand-Id': storeBrandId } }
    );
};

export const saveBrandAgentConfig = async (
    request: SaveBrandAgentConfigRequest
): Promise<SaveBrandAgentConfigResponse> => {
    return apiClient.post<SaveBrandAgentConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.SAVE_BRAND_AGENT_CONFIG(),
        request,
        { headers: { 'X-Store-Brand-Id': request.storeBrandId } }
    );
};

export const getSaleCompletionWhatsAppConfig = async (
    storeId: string,
    storeBrandId: string
): Promise<GetSaleCompletionWhatsAppConfigResponse> => {
    return apiClient.get<GetSaleCompletionWhatsAppConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_SALE_COMPLETION_WHATSAPP_CONFIG(storeId),
        brandHeaders(storeBrandId)
    );
};

export const updateSaleCompletionWhatsAppConfig = async (
    storeId: string,
    storeBrandId: string,
    saleCompletionWhatsApp: SaleCompletionWhatsAppConfig
): Promise<UpdateSaleCompletionWhatsAppConfigResponse> => {
    const request: UpdateSaleCompletionWhatsAppConfigRequest = { saleCompletionWhatsApp };
    return apiClient.patch<UpdateSaleCompletionWhatsAppConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.UPDATE_SALE_COMPLETION_WHATSAPP_CONFIG(storeId),
        request,
        brandHeaders(storeBrandId)
    );
};

export const getMultiStoreAgentConfig = async (
    storeId: string,
    storeBrandId: string
): Promise<GetMultiStoreAgentConfigResponse> => {
    return apiClient.get<GetMultiStoreAgentConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_MULTI_STORE_AGENT_CONFIG(storeId),
        brandHeaders(storeBrandId)
    );
};

export const updateMultiStoreAgentConfig = async (
    storeId: string,
    storeBrandId: string,
    multiStoreAgentConfig: MultiStoreAgentConfigPayload
): Promise<UpdateMultiStoreAgentConfigResponse> => {
    const request: UpdateMultiStoreAgentConfigRequest = { multiStoreAgentConfig };
    return apiClient.patch<UpdateMultiStoreAgentConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.UPDATE_MULTI_STORE_AGENT_CONFIG(storeId),
        request,
        brandHeaders(storeBrandId)
    );
};

export const getLoginPolicy = async (
    storeBrandId: string
): Promise<GetLoginPolicyResponse> => {
    return apiClient.get<GetLoginPolicyResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_LOGIN_POLICY(storeBrandId),
        brandHeaders(storeBrandId)
    );
};

export const saveLoginPolicy = async (
    storeBrandId: string,
    request: SaveLoginPolicyRequest
): Promise<GetLoginPolicyResponse> => {
    return apiClient.patch<GetLoginPolicyResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.SAVE_LOGIN_POLICY(storeBrandId),
        request,
        brandHeaders(storeBrandId)
    );
};

export const getMovementCountConfig = async (
    storeBrandId: string
): Promise<GetMovementCountConfigResponse> => {
    return apiClient.get<GetMovementCountConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_MOVEMENT_COUNT_CONFIG(storeBrandId),
        brandHeaders(storeBrandId)
    );
};

export const saveMovementCountConfig = async (
    storeBrandId: string,
    request: SaveMovementCountConfigRequest
): Promise<SaveMovementCountConfigResponse> => {
    return apiClient.patch<SaveMovementCountConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.SAVE_MOVEMENT_COUNT_CONFIG(storeBrandId),
        request,
        brandHeaders(storeBrandId)
    );
};

export const getDailyNotificationSettings = async (
    storeBrandId: string
): Promise<GetDailyNotificationSettingsResponse> => {
    return apiClient.get<GetDailyNotificationSettingsResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_DAILY_NOTIFICATION_SETTINGS(storeBrandId),
        brandHeaders(storeBrandId)
    );
};

export const updateDailyNotificationSettings = async (
    storeBrandId: string,
    settings: DailyNotificationSettings
): Promise<UpdateDailyNotificationSettingsResponse> => {
    return apiClient.patch<UpdateDailyNotificationSettingsResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.UPDATE_DAILY_NOTIFICATION_SETTINGS(storeBrandId),
        settings,
        brandHeaders(storeBrandId)
    );
};

export const getStoreOperationsConfig = async (
    storeId: string,
    storeBrandId: string
): Promise<GetStoreOperationsConfigResponse> => {
    return apiClient.get<GetStoreOperationsConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_STORE_OPERATIONS_CONFIG(storeId),
        brandHeaders(storeBrandId)
    );
};

export const updateStoreOperationsConfig = async (
    storeId: string,
    storeBrandId: string,
    operationsConfig: StoreOperationsConfig
): Promise<GetStoreOperationsConfigResponse> => {
    return apiClient.patch<GetStoreOperationsConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.UPDATE_STORE_OPERATIONS_CONFIG(storeId),
        { operationsConfig },
        brandHeaders(storeBrandId)
    );
};

export const getStoreBrandOperationsConfig = async (
    storeBrandId: string
): Promise<GetStoreBrandOperationsConfigResponse> => {
    return apiClient.get<GetStoreBrandOperationsConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_BRAND_OPERATIONS_CONFIG(storeBrandId),
        brandHeaders(storeBrandId)
    );
};

export const updateStoreBrandOperationsConfig = async (
    storeBrandId: string,
    operationsConfig: StoreBrandOperationsConfig
): Promise<GetStoreBrandOperationsConfigResponse> => {
    return apiClient.patch<GetStoreBrandOperationsConfigResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.UPDATE_BRAND_OPERATIONS_CONFIG(storeBrandId),
        { operationsConfig },
        brandHeaders(storeBrandId)
    );
};

export const getDashboardExpiry = async (
    storeBrandId: string
): Promise<GetDashboardExpiryResponse> => {
    return apiClient.get<GetDashboardExpiryResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_DASHBOARD_EXPIRY(storeBrandId),
        brandHeaders(storeBrandId)
    );
};

export const updateDashboardExpiry = async (
    storeBrandId: string,
    settings: UpdateDashboardExpiryRequest
): Promise<UpdateDashboardExpiryResponse> => {
    return apiClient.patch<UpdateDashboardExpiryResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.UPDATE_DASHBOARD_EXPIRY(storeBrandId),
        settings,
        brandHeaders(storeBrandId)
    );
};

export const getStoreMembers = async (
    storeBrandId: string,
    options?: { storeId?: string; searchText?: string }
): Promise<StoreMember[]> => {
    const request: GetStoreMembersRequest = {
        storeBrandID: storeBrandId,
        ...(options?.storeId ? { storeID: options.storeId } : {}),
        ...(options?.searchText ? { searchText: options.searchText } : {}),
    };

    const response = await apiClient.post<GetStoreMembersResponse>(
        API_ENDPOINTS.STORE_MANAGEMENT.GET_STORE_MEMBERS,
        request,
        { headers: { 'X-Store-Brand-Id': storeBrandId } }
    );
    return response.data?.storeMembers ?? [];
};
